import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEventStore } from '../../store/eventStore';
import { useFamilyStore } from '../../store/familyStore';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';
import { CalendarEvent, CalendarView } from '../../types/calendar';
import { addDays, addMonths, isToday } from '../../utils/calendar';
import MobileDayView from '../../components/calendar/MobileDayView';
import MobileWeekView from '../../components/calendar/MobileWeekView';
import BottomSheet from '../../components/mobile/BottomSheet';
import EventPreview from '../../components/calendar/EventPreview';
import { enrichEventsWithFamilyData } from '../../utils/enrichEvents';
import DatePicker from '../../components/DatePicker';
import { useTodoStore } from '../../store/todoStore';
import { isExpandedRecurringInstance } from '../../utils/recurringInstance';
import { useToastStore } from '../../store/toastStore';

export default function MobileCalendar() {
  const navigate = useNavigate();
  const location = useLocation();
  const rawEvents = useEventStore((state) => state.events);
  const fetchEvents = useEventStore((state) => state.fetchEvents);
  const isLoading = useEventStore((state) => state.isLoading);
  const deleteEvent = useEventStore((state) => state.deleteEvent);
  const isDeleting = useEventStore((state) => state.isDeleting);
  const { family } = useFamilyStore();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const { showToast, dismissToast } = useToastStore();
  
  // Enrich events with current family member data (colors, names, avatars)
  const events = useMemo(() => {
    if (!family?.members) {
      return rawEvents;
    }
    return enrichEventsWithFamilyData(rawEvents, family.members);
  }, [rawEvents, family?.members]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>('day');
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showEventPreview, setShowEventPreview] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const { todos } = useTodoStore();
  
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const touchEndY = useRef<number>(0);
  
  // Handle navigation state for "Today" nav item
  const hasProcessedNavState = useRef(false);
  useEffect(() => {
    const navState = location.state as { view?: CalendarView; date?: Date | string } | null;
    if (navState?.view === 'day' && navState?.date && !hasProcessedNavState.current) {
      const targetDate = typeof navState.date === 'string' ? new Date(navState.date) : navState.date;
      setCurrentDate(targetDate);
      setView('day');
      hasProcessedNavState.current = true;
      // Store in sessionStorage for active state detection in nav
      sessionStorage.setItem('mobile-calendar-view', 'day');
      sessionStorage.setItem('mobile-calendar-date', targetDate.toISOString());
      // Clear the state to prevent reprocessing on re-renders
      window.history.replaceState({}, '', location.pathname);
    }
    // Reset the ref when location changes (new navigation)
    return () => {
      hasProcessedNavState.current = false;
    };
  }, [location.state, location.pathname]);
  
  // Clear sessionStorage when view or date changes (user navigates away from today)
  useEffect(() => {
    const storedView = sessionStorage.getItem('mobile-calendar-view');
    const storedDate = sessionStorage.getItem('mobile-calendar-date');
    if (storedView === 'day' && storedDate) {
      const storedDateObj = new Date(storedDate);
      // Clear if view changed or date is no longer today
      if (view !== 'day' || !isToday(currentDate) || !isToday(storedDateObj)) {
        sessionStorage.removeItem('mobile-calendar-view');
        sessionStorage.removeItem('mobile-calendar-date');
      }
    }
  }, [view, currentDate]);

  // Fetch events based on current view
  useEffect(() => {
    // CRITICAL FIX: Fetch family if not loaded
    const { fetchFamily } = useFamilyStore.getState();
    if (!family && !useFamilyStore.getState().isLoading) {
      fetchFamily().catch((error) => {
        console.error('Error fetching family:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load family', 'error');
      });
      return; // Wait for family to load
    }
    
    if (family) {
      let startDate: string;
      let endDate: string;
      
      // Create new Date objects to avoid mutation issues
      const date = new Date(currentDate);
      
      if (view === 'day') {
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
        const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
        startDate = dayStart.toISOString().split('T')[0];
        endDate = dayEnd.toISOString().split('T')[0];
      } else if (view === 'week') {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        startDate = weekStart.toISOString().split('T')[0];
        endDate = weekEnd.toISOString().split('T')[0];
      } else {
        // Month view
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        const daysToSubtract = firstDay.getDay();
        const daysToAdd = 42 - (lastDay.getDate() + daysToSubtract);
        firstDay.setDate(firstDay.getDate() - daysToSubtract);
        lastDay.setDate(lastDay.getDate() + daysToAdd);
        startDate = firstDay.toISOString().split('T')[0];
        endDate = lastDay.toISOString().split('T')[0];
      }

      fetchEvents(startDate, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [family, view, currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()]);

  const handlePrevious = (e?: React.MouseEvent | React.TouchEvent) => {
    // Prevent event propagation to avoid double-triggering
    if (e) {
      e.stopPropagation();
    }
    
    if (view === 'day') {
      setCurrentDate(prevDate => {
        const newDate = new Date(prevDate);
        return addDays(newDate, -1);
      });
    } else if (view === 'week') {
      setCurrentDate(prevDate => {
        const newDate = new Date(prevDate);
        return addDays(newDate, -7);
      });
    } else {
      setCurrentDate(prevDate => {
        const newDate = new Date(prevDate);
        return addMonths(newDate, -1);
      });
    }
  };

  const handleNext = (e?: React.MouseEvent | React.TouchEvent) => {
    // Prevent event propagation to avoid double-triggering
    if (e) {
      e.stopPropagation();
    }
    
    if (view === 'day') {
      setCurrentDate(prevDate => {
        const newDate = new Date(prevDate);
        return addDays(newDate, 1);
      });
    } else if (view === 'week') {
      setCurrentDate(prevDate => {
        const newDate = new Date(prevDate);
        return addDays(newDate, 7);
      });
    } else {
      setCurrentDate(prevDate => {
        const newDate = new Date(prevDate);
        return addMonths(newDate, 1);
      });
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date: Date, hour?: number) => {
    navigate('/mobile/create', { 
      state: { 
        initialDate: hour ? new Date(date.setHours(hour, 0, 0, 0)) : date 
      } 
    });
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowEventPreview(true);
  };

  const handleDelete = async (event: CalendarEvent) => {
    const isInstance = isExpandedRecurringInstance(event);
    let instanceOnly = false;

    if (isInstance) {
      const deleteSeries = window.confirm(
        'Delete the entire series? Press Cancel to delete only this event.'
      );
      if (deleteSeries) {
        if (!window.confirm('This will delete all occurrences. Continue?')) {
          return;
        }
        instanceOnly = false;
      } else {
        if (!window.confirm('Delete this event occurrence?')) {
          return;
        }
        instanceOnly = true;
      }
    } else {
      const confirmDelete = window.confirm(
        event.isRecurring ? 'Delete this recurring event and all occurrences?' : 'Delete this event?'
      );
      if (!confirmDelete) {
        return;
      }
    }

    const toastId = showToast('Deleting event...', 'loading');
    try {
      await deleteEvent(event.id, instanceOnly);
      dismissToast(toastId);
      showToast('Event deleted', 'success');
      setShowEventPreview(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      dismissToast(toastId);
      showToast('Failed to delete event. Please try again.', 'error');
    }
  };

  // Swipe gesture handlers - require horizontal movement to dominate over vertical
  // to avoid triggering day change when user is scrolling up/down
  const handleTouchStart = (e: React.TouchEvent) => {
    // Don't track touch if it's on a button or interactive element
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Don't track touch if it's on a button or interactive element
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Don't process swipe if it's on a button or interactive element
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }

    const deltaX = touchEndX.current - touchStartX.current;
    const deltaY = touchEndY.current - touchStartY.current;
    const threshold = 80; // Minimum horizontal swipe distance (increased from 50)
    const horizontalDominanceRatio = 2; // Horizontal must be 2x vertical to count as swipe

    // Only trigger day change if horizontal movement clearly dominates vertical
    // (avoids accidental day switch when scrolling up/down)
    const isHorizontalSwipe =
      Math.abs(deltaX) > threshold &&
      Math.abs(deltaX) > Math.abs(deltaY) * horizontalDominanceRatio;

    if (isHorizontalSwipe) {
      if (deltaX > 0) {
        // Swipe right - go to previous
        handlePrevious();
      } else {
        // Swipe left - go to next
        handleNext();
      }
    }
  };

  return (
    <div 
      className="h-full flex flex-col overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header with Navigation */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10 px-4 py-2 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handlePrevious(e);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Only handle touch if click didn't already fire
                if (!(e.target as HTMLElement).hasAttribute('data-clicked')) {
                  (e.target as HTMLElement).setAttribute('data-clicked', 'true');
                  handlePrevious(e);
                  setTimeout(() => {
                    (e.target as HTMLElement).removeAttribute('data-clicked');
                  }, 300);
                }
              }}
              className="w-10 h-10 flex items-center justify-center rounded-landing bg-gray-100 active:bg-gray-200 min-h-[44px]"
              aria-label="Previous"
            >
              ←
            </button>
            <button
              onClick={handleToday}
              className="px-4 py-2 bg-gray-100 rounded-landing font-semibold text-sm active:bg-gray-200 min-h-[44px]"
            >
              Today
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleNext(e);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Only handle touch if click didn't already fire
                if (!(e.target as HTMLElement).hasAttribute('data-clicked')) {
                  (e.target as HTMLElement).setAttribute('data-clicked', 'true');
                  handleNext(e);
                  setTimeout(() => {
                    (e.target as HTMLElement).removeAttribute('data-clicked');
                  }, 300);
                }
              }}
              className="w-10 h-10 flex items-center justify-center rounded-landing bg-gray-100 active:bg-gray-200 min-h-[44px]"
              aria-label="Next"
            >
              →
            </button>
          </div>
          
          {/* View Switcher */}
          <div className="flex gap-1 bg-gray-100 rounded-landing p-1">
            {(['day', 'week', 'month'] as CalendarView[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 rounded-landing text-xs font-semibold min-h-[36px] transition-all ${
                  view === v
                    ? `bg-white ${tokens.text.accent} shadow-sm`
                    : 'text-gray-600 active:bg-gray-200'
                }`}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar View */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {view === 'day' && (
          <MobileDayView
            date={currentDate}
            events={events}
            onDateClick={handleDateClick}
            onEventClick={handleEventClick}
          />
        )}
        {view === 'week' && (
          <MobileWeekView
            date={currentDate}
            events={events}
            onDateClick={handleDateClick}
            onEventClick={handleEventClick}
          />
        )}
        {view === 'month' && (
          <div className="h-full flex items-center justify-center text-gray-500">
            <p>Month view coming soon</p>
          </div>
        )}
      </div>

      {/* Event Preview Bottom Sheet */}
      {selectedEvent && (
        <BottomSheet
          isOpen={showEventPreview}
          onClose={() => {
            setShowEventPreview(false);
            setSelectedEvent(null);
          }}
          title="Event Details"
        >
          <div className="p-3">
            <EventPreview
              event={selectedEvent}
              onEdit={() => {
                setShowEventPreview(false);
                // Navigate to edit - would need to implement
              }}
              onDelete={() => handleDelete(selectedEvent)}
              onClose={() => {
                setShowEventPreview(false);
                setSelectedEvent(null);
              }}
              isDarkMode={isDarkMode}
              isDeleting={isDeleting === selectedEvent.id}
            />
          </div>
        </BottomSheet>
      )}

      {/* Date Picker Modal */}
      <DatePicker
        isOpen={showDatePicker}
        currentDate={currentDate}
        onDateSelect={(date) => {
          setCurrentDate(date);
          setShowDatePicker(false);
        }}
        onClose={() => setShowDatePicker(false)}
        events={events}
        todos={todos}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}
